import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
// darkreader is imported ON DEMAND inside applyTheme(): it touches `window`
// at import time (crashes prerendering) and weighs ~150KB that only dark
// theme users need.

export type Theme = 'light' | 'dark' | 'system';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private themeSubject = new BehaviorSubject<Theme>('light');
  public theme$ = this.themeSubject.asObservable();

  constructor() {
    // Prerendering always bakes the light theme; the saved preference is
    // re-applied in the browser (darkreader + localStorage are DOM-only).
    if (this.isBrowser) {
      this.initializeTheme();
    }
  }

  private initializeTheme() {
    const savedTheme = localStorage.getItem('theme') as Theme;
    if (savedTheme) {
      this.setTheme(savedTheme);
    } else {
      this.setTheme('light');
    }
  }

  setTheme(theme: Theme) {
    this.themeSubject.next(theme);
    if (!this.isBrowser) {
      return;
    }
    localStorage.setItem('theme', theme);
    this.applyTheme(theme);
  }

  private async applyTheme(theme: Theme) {
    const {
      enable: enableDarkReader,
      disable: disableDarkReader,
      auto: followSystemColorScheme,
    } = await import('darkreader');
    if (theme === 'dark') {
      enableDarkReader({
        brightness: 100,
        contrast: 100,
        sepia: 0,
      });
    } else if (theme === 'light') {
      disableDarkReader();
    } else if (theme === 'system') {
      followSystemColorScheme({
        brightness: 100,
        contrast: 100,
        sepia: 0,
      });
    }
  }

  getCurrentTheme(): Theme {
    return this.themeSubject.value;
  }

  toggleTheme() {
    const currentTheme = this.getCurrentTheme();
    // Simple toggle between light and dark for the button
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    this.setTheme(newTheme);
  }
}
